import React from "react";
import { motion } from "framer-motion";
import ParticleField from "./ParticleField";
import { useI18n } from "@/i18n/LanguageContext";

const headline = ["The", "Orb"];

const coords = [
  { t: "12%", l: "38%", label: "23", d: 1.1 },
  { t: "17%", r: "26%", label: "36", d: 1.3 },
  { t: "34%", l: "20%", label: "84", d: 1.5 },
  { b: "26%", r: "30%", label: "52", d: 1.2 },
  { b: "18%", l: "30%", label: ".76", d: 1.4 },
  { b: "10%", l: "44%", label: "63", d: 1.6 },
];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.5 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { t } = useI18n();
  return (
    <section
      className="relative h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* liquid metal texture */}
      <div className="absolute inset-0 opacity-[0.14] mask-radial-fade">
        <img
          src="https://images.unsplash.com/photo-1637750586228-179a1a7aef84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbGlxdWlkJTIwbWV0YWwlMjB0ZXh0dXJlfGVufDB8fHxibGFja3wxNzg0ODI3MDkzfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-full w-full object-cover"
        />
      </div>

      {/* particle canvas */}
      <ParticleField />

      {/* mono coordinate labels */}
      {coords.map((c, i) => (
        <motion.span
          key={i}
          className="pointer-events-none absolute z-10 font-mono-accent text-[11px] tracking-widest text-white/45"
          style={{ top: c.t, bottom: c.b, left: c.l, right: c.r }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.6, 0.35, 0.6] }}
          transition={{
            opacity: { delay: 1.4 + i * 0.1, duration: 3 + c.d, repeat: Infinity, repeatType: "reverse" },
          }}
        >
          {c.label}
        </motion.span>
      ))}

      {/* eyebrow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 1 }}
        className="absolute right-6 sm:right-16 top-[26%] z-20 text-right"
        data-testid="hero-eyebrow"
      >
        <p className="font-mono-accent text-[11px] uppercase leading-relaxed tracking-[0.25em] text-[#a855f7]">
          Orb Holdings
          <br />
          Limited
        </p>
      </motion.div>

      {/* headline */}
      <div className="absolute right-6 sm:right-16 bottom-[24%] sm:bottom-[22%] z-20 text-right">
        {headline.map((line, i) => (
          <span key={i} className="line-mask">
            <motion.span
              custom={i}
              variants={lineReveal}
              initial="hidden"
              animate="show"
              className="hero-display block text-white"
            >
              {line}
            </motion.span>
          </span>
        ))}
      </div>

      {/* microcopy */}
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute right-6 sm:right-16 bottom-[9%] z-20 max-w-[440px] text-right font-mono-accent text-[13px] uppercase leading-relaxed tracking-[0.25em] text-white/55"
        data-testid="hero-microcopy"
      >
        {t.hero.tagline}
      </motion.p>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute left-1/2 bottom-6 z-20 -translate-x-1/2 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/35"
      >
        <span className="flex flex-col items-center gap-2">
          {t.hero.scroll}
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="block h-6 w-px bg-white/25"
          />
        </span>
      </motion.div>
    </section>
  );
}
